import { 
  Sparkles,
  BarChartBig,
  FileText,
  Lightbulb,
  ShieldCheck,
  ArrowRight,
  Clock,
  BrainCircuit,
  Target,
  TrendingUp,
  Users,
  Building2,
  FileEdit,
  Database
} from 'lucide-react';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';

const features = [
  {
    name: 'Complete Hiring Dashboard',
    description: 'Comprehensive dashboard to manage all your hiring projects, track candidates, and monitor team performance with real-time analytics.',
    icon: Target,
    iconColor: 'text-slate-600 dark:text-slate-400',
    bgColor: 'bg-slate-100/50 dark:bg-slate-900/20',
    metrics: ['Project tracking', 'Team collaboration'],
    cta: 'See dashboard demo'
  },
  {
    name: 'AI-Powered Resume Analysis',
    description: 'Advanced technology that analyzes resumes with context understanding, skill extraction, and experience verification for accurate candidate assessment.',
    icon: BrainCircuit,
    iconColor: 'text-purple-500 dark:text-purple-400',
    bgColor: 'bg-purple-100/50 dark:bg-purple-900/20',
    metrics: ['98% accuracy', '90% time saving'],
    cta: 'See analysis demo'
  },
  {
    name: 'Intelligent Matching & Scoring',
    description: 'Receive detailed candidate-to-job fit scores with visual breakdowns of qualification matches, skill gaps, and compatibility analysis.',
    icon: BarChartBig,
    iconColor: 'text-green-500 dark:text-green-400',
    bgColor: 'bg-green-100/50 dark:bg-green-900/20',
    metrics: ['82% better hires', '45% reduced turnover'],
    cta: 'View scoring example'
  },
  {
    name: 'Market Intelligence Hub',
    description: 'Access comprehensive salary insights, talent trends, company analytics, and competitive market data to make informed hiring decisions.',
    icon: TrendingUp,
    iconColor: 'text-blue-500 dark:text-blue-400',
    bgColor: 'bg-blue-100/50 dark:bg-blue-900/20',
    metrics: ['Real-time data', 'Industry insights'],
    cta: 'Explore market data'
  },
  {
    name: 'Smart Job Description Generator',
    description: 'Generate optimized, bias-free job descriptions in seconds. Attract better candidates with ATS-friendly listings that stand out.',
    icon: FileEdit,
    iconColor: 'text-indigo-500 dark:text-indigo-400',
    bgColor: 'bg-indigo-100/50 dark:bg-indigo-900/20',
    metrics: ['75% time saving', '35% more applications'],
    cta: 'Try JD generator'
  },
  {
    name: 'Professional Report Generation',
    description: 'Generate comprehensive, shareable reports with candidate analysis, side-by-side comparisons, and actionable insights in seconds.',
    icon: FileText,
    iconColor: 'text-emerald-500 dark:text-emerald-400',
    bgColor: 'bg-emerald-100/50 dark:bg-emerald-900/20',
    metrics: ['Instant generation', 'Collaboration ready'],
    cta: 'View sample report'
  },
  {
    name: 'Team Collaboration Tools',
    description: 'Share insights, collaborate on hiring decisions, track team progress, and maintain hiring consistency across your organization.',
    icon: Users,
    iconColor: 'text-amber-500 dark:text-amber-400',
    bgColor: 'bg-amber-100/50 dark:bg-amber-900/20',
    metrics: ['Team sync', 'Decision tracking'],
    cta: 'See collaboration features'
  },
  {
    name: 'Advanced Analytics & Insights',
    description: 'Deep analytics on hiring performance, candidate quality metrics, time-to-hire tracking, and ROI measurement for data-driven decisions.',
    icon: Database,
    iconColor: 'text-red-500 dark:text-red-400',
    bgColor: 'bg-red-100/50 dark:bg-red-900/20',
    metrics: ['Performance tracking', 'ROI insights'],
    cta: 'View analytics'
  },
  {
    name: 'Custom Interview Plans',
    description: 'Generate expertly tailored interview questions for each candidate profile, with suggested evaluation criteria and red flags to watch for.',
    icon: Lightbulb,
    iconColor: 'text-orange-500 dark:text-orange-400',
    bgColor: 'bg-orange-100/50 dark:bg-orange-900/20',
    metrics: ['60% better interviews', '93% user satisfaction'],
    cta: 'See example plan'
  },
  {
    name: 'Company Intelligence',
    description: 'Track companies, analyze competitors, understand talent pools, and gain insights into market movements and hiring patterns.',
    icon: Building2,
    iconColor: 'text-cyan-500 dark:text-cyan-400',
    bgColor: 'bg-cyan-100/50 dark:bg-cyan-900/20',
    metrics: ['Company tracking', 'Talent insights'],
    cta: 'Explore company data'
  },
  {
    name: 'Lightning-Fast Processing',
    description: 'Analyze hundreds of resumes in minutes, not days. Scale your recruitment efforts without adding headcount or compromising quality.',
    icon: Clock,
    iconColor: 'text-violet-500 dark:text-violet-400',
    bgColor: 'bg-violet-100/50 dark:bg-violet-900/20',
    metrics: ['70% faster hiring', '85% cost reduction'],
    cta: 'See it in action'
  },
  {
    name: 'Bias Prevention System',
    description: 'Proprietary technology ensures fair and objective candidate evaluation by focusing on skills and experience, not demographic indicators.',
    icon: ShieldCheck,
    iconColor: 'text-teal-500 dark:text-teal-400',
    bgColor: 'bg-teal-100/50 dark:bg-teal-900/20',
    metrics: ['100% compliance', 'Diverse hiring'],
    cta: 'Learn about fairness'
  }
];

export function FeaturesGrid() {
  return (
    <div className="bg-muted/30 py-24 sm:py-32" id="features">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 sm:mb-20">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            <Sparkles className="h-4 w-4 mr-2" />
            Cutting-Edge Technology
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground leading-tight">
            Recruitment <span className="text-primary">Supercharged</span> with Smart Tech
          </h2>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground mx-auto">
            Leverage advanced technology to find the perfect candidates quickly and accurately, without bias or guesswork.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {features.map((feature) => (
            <div
              key={feature.name}
              className={cn(
                "relative group p-6 rounded-xl border border-border bg-card overflow-hidden",
                "hover:border-primary/40 hover:shadow-lg transition-all duration-300",
                "flex flex-col h-full"
              )}
            >
              {/* Subtle accent background */}
              <div className="absolute -right-8 -top-8 w-16 h-16 rounded-full opacity-10 bg-gradient-to-br from-primary to-primary/50 blur-xl group-hover:opacity-20 transition-opacity" />
              
              <div className={cn(
                "mb-5 inline-flex items-center justify-center p-3 rounded-lg",
                feature.bgColor,
              )}>
                <feature.icon className={cn("h-6 w-6", feature.iconColor)} aria-hidden="true" />
              </div>
              
              <div className="flex-grow">
                <h3 className="text-lg font-semibold text-foreground tracking-tight mb-2">
                  {feature.name}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
                
                {/* Metrics */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {feature.metrics.map((metric, i) => (
                    <div key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/10">
                      {metric}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* CTA link */}
              <div className="mt-5 pt-4 border-t border-border/50">
                <Button variant="ghost" size="sm" className="w-full justify-between group/btn text-primary/90 hover:text-primary">
                  {feature.cta}
                  <ArrowRight className="h-4 w-4 ml-1 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Button size="lg" asChild className="group">
            <Link to="/signup">
              Start Your Free Trial
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
          <p className="mt-4 text-sm text-muted-foreground">
            No credit card required • 4 free resume matches • Cancel anytime
          </p>
        </div>
      </div>
    </div>
  );
}